import { usePrintDocument } from '@Hooks/Documents';
import { useDocumentsRepository } from '@Hooks/useRepository';
import type { ApiPrintOption } from '@Model';
import { PrintOptionsRepository } from '@Repositories/Documents';
import { useToasts } from '@synqo/frontend-component-library';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

/** The print option of an invoice snapshot's own document, which is also its category. */
export const INVOICE_SNAPSHOT_CATEGORY = 'invoice_snapshot';

/** Downloads the document of one invoice snapshot, looking up its print option on click. */
// @ai-generated 2026-09-29T07:37:25+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function useInvoiceSnapshotDocument() {
    const printOptions = useDocumentsRepository(PrintOptionsRepository);
    const queryClient = useQueryClient();
    const printDocument = usePrintDocument();
    const { pushToast } = useToasts();

    const [isResolving, setIsResolving] = useState(false);

    const resolveOption = useCallback(
        async (snapshotId: string): Promise<ApiPrintOption | undefined> => {
            setIsResolving(true);
            try {
                const result = await queryClient.fetchQuery({
                    queryKey: ['documents', 'options', INVOICE_SNAPSHOT_CATEGORY, snapshotId],
                    queryFn: () =>
                        printOptions.getPrintOptionsInCategory({ primary_id: snapshotId }, INVOICE_SNAPSHOT_CATEGORY),
                });

                const option = result?.data?.[0];
                if (option === undefined) {
                    pushToast({ message: 'Zu dieser Rechnung liegt kein Dokument vor.', variant: 'warning' });
                }
                return option;
            } finally {
                setIsResolving(false);
            }
        },
        [printOptions, queryClient, pushToast],
    );

    const download = useCallback(
        async (snapshotId: string) => {
            const option = await resolveOption(snapshotId);
            if (option !== undefined) {
                await printDocument({ printOption: option.id, primaryId: option.related_object_id, mode: 'download' });
            }
        },
        [resolveOption, printDocument],
    );

    return { download, isResolving };
}
